/**
 * An INSTALLED fill, mounted in the host realm (RFC 0284 S6a).
 *
 * The sibling of `App`, and deliberately not a reuse of it. That one
 * mounts an EDITOR: it takes a document id, opens a handle on it, and hands the
 * app a comments surface targeted at that document. A sidebar has no document
 * and a thread renderer's subject is a session, so three of its five arguments
 * would be lies. What the two share is the loading trick, which is eight lines,
 * and copying eight lines beats widening a contract until it means nothing.
 *
 * WHAT A CONTRIBUTION GETS is a container and `mod.window`. That is the whole
 * surface, and it is small on purpose: the RFC's admission test is that a
 * method exists because shipped chrome calls it, and shipped chrome calls the
 * window and nothing else it could not get from the capabilities apps already
 * have.
 *
 * A MODULE, NOT AN IFRAME, for the reason RFC 0275 gives: chrome that cannot
 * hold a live handle cannot draw a list that updates. The sandboxed tier can
 * carry JSON and a sidebar needs the working set as it changes.
 */

import { useEffect, useRef, useState } from 'react'
import type { DocsCapability } from '../docs'
import type { RecentsCapability } from '../recents'
import type { SessionCapability } from '../session'
import type { SessionsCapability } from '../sessions'
import type { SettingsCapability } from '../settings'
import { useDocs, useRecents, useSessions, useWindow } from '../WindowProvider'
import type { WindowCapability } from '../window'

/** What a fill's module must export. */
interface ContributionModule {
  default?: (ctx: {
    container: HTMLElement
    window: WindowCapability
    docs: DocsCapability
    /** The window's sessions (§4g). The other half of what a person works on:
     *  a sidebar or header that can list documents and not threads is half a
     *  replacement for the one we ship. */
    sessions: SessionsCapability
    /** The working set: what has been reached for in this space, most recent
     *  first (S4c). Ids, resolved against `docs` or `sessions`. */
    recents: RecentsCapability
    /** What THIS app was configured to do (S6g). Per app, not per window, so it
     *  comes from whatever mounted it rather than from a context. */
    settings: SettingsCapability
    /** Only for a LEAF renderer (S6b): the session this pane is drawing. Absent
     *  for chrome, because a sidebar has no one subject. */
    session?: SessionCapability
    onDispose: (fn: () => void) => void
  }) => void
}

export function Mount({
  code,
  title,
  settings,
  session,
}: {
  /** The module source, from the fill's view document. */
  code: string
  /** Shown if it fails, so a broken sidebar says whose it is. */
  title: string
  /** This app's settings (S6g). From the mount, because they are the APP's. */
  settings: SettingsCapability
  /** Passed straight through when the mount is a thread rather than chrome
   *  (S6b). Its identity must be stable for the module's life; `Thread`
   *  is what guarantees that. */
  session?: SessionCapability
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const window = useWindow()
  const docs = useDocs()
  const sessionList = useSessions()
  const recents = useRecents()

  // Through a ref, so a shell re-render does not remount the fill. The
  // capability's identity is already stable (see `WindowProvider`); this guards
  // the case where it is not, because a remounting sidebar loses its scroll
  // position and any state it holds on every keystroke.
  const windowRef = useRef(window)
  windowRef.current = window
  const docsRef = useRef(docs)
  docsRef.current = docs
  const sessionRef = useRef(session)
  sessionRef.current = session
  const sessionListRef = useRef(sessionList)
  sessionListRef.current = sessionList
  const settingsRef = useRef(settings)
  settingsRef.current = settings
  const recentsRef = useRef(recents)
  recentsRef.current = recents

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    let cancelled = false
    let objectUrl: string | null = null
    const disposers: (() => void)[] = []

    const boot = async () => {
      objectUrl = URL.createObjectURL(
        new Blob([code], { type: 'text/javascript' }),
      )
      const mod = (await import(
        /* @vite-ignore */ objectUrl
      )) as ContributionModule
      if (cancelled) return

      if (typeof mod.default !== 'function') {
        throw new Error(
          'a fill must export default a mount function; ' +
            `got ${typeof mod.default}`,
        )
      }
      const win = windowRef.current
      const documents = docsRef.current
      const sessions = sessionListRef.current
      const recentList = recentsRef.current
      if (!win || !documents || !sessions || !recentList) {
        // Only reachable outside a `WindowProvider`, which is a mounting bug
        // rather than a bad app. Said plainly so it is not mistaken for one.
        throw new Error('no capabilities: not inside a WindowProvider')
      }
      mod.default({
        container,
        window: win,
        docs: documents,
        sessions,
        recents: recentList,
        settings: settingsRef.current,
        // Only when there IS one. A chrome fill should not be handed a
        // `session` key at all, rather than one that is null: a module can then
        // tell "I am a thread" from "I am a sidebar" by asking.
        ...(sessionRef.current ? { session: sessionRef.current } : {}),
        onDispose: (fn) => disposers.push(fn),
      })
    }

    void boot().catch((err) => {
      // SHOWN, not swallowed. Chrome that fails silently is chrome that is
      // simply missing, and a person cannot tell that from having emptied the
      // slot themselves.
      if (!cancelled)
        setFailure(err instanceof Error ? err.message : String(err))
    })

    return () => {
      cancelled = true
      for (const dispose of disposers) {
        try {
          dispose()
        } catch {
          // A fill that throws on the way out must not stop the next
          // one from being torn down, or a slot leaks a mount per switch.
        }
      }
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [code])

  if (failure) {
    return (
      <div className="flex h-full flex-col justify-center gap-1 bg-background p-3 text-[11px] text-muted-foreground">
        <span className="font-medium text-foreground">{title} failed</span>
        <span className="leading-snug">{failure}</span>
        <span className="leading-snug">
          Pick a different layout in Settings (⌘,) to get your chrome back.
        </span>
      </div>
    )
  }

  /**
   * THE SLOT SUPPLIES THE SURFACE, not the fill.
   *
   * `bg-background` and `text-foreground` were missing, and the result was a
   * transparent column whose text composited over the panes behind it: drawn
   * correctly, and unreadable. The built-in gets these from `Panel`; an
   * installed fill had nothing.
   *
   * It belongs here rather than in each fill. A module that styles
   * nothing should still look like chrome, and requiring every author to learn
   * our CSS variables to be legible would make the first thing anyone writes
   * look broken.
   *
   * The theme's own tokens, so an installed sidebar follows light and dark
   * without being told, and a font size so a fill that sets none is not
   * rendering at whatever the page happens to inherit.
   */
  return (
    <div
      ref={containerRef}
      className="h-full w-full overflow-hidden bg-background text-[12px] text-foreground"
    />
  )
}
